import { useState } from "react";
import { ArrowRight } from "lucide-react";
import PaymentModal from "@/components/PaymentModal";
import KundliComparison from "@/components/KundliComparison";
import VastuOutstationCards from "@/components/VastuOutstationCards";
import VastuEnquiryForm from "@/components/VastuEnquiryForm";
import useSEO from "@/hooks/useSEO";
import { ASTROLOGY_SERVICES, ASTROCARTOGRAPHY_SERVICES, VASTU_RESIDENTIAL, VASTU_COMMERCIAL, VASTU_INDUSTRIAL, PREMIUM_KUNDLI, YEARLY_ASTROLOGY, WHATSAPP_URL } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

function SectionHeader({ overline, title, dark }) {
  return (
    <div style={{ marginBottom: "40px" }}>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "8px" }}>{overline}</p>
      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: dark ? "#FDFCF8" : CHARCOAL }}>{title}</h2>
    </div>
  );
}

function ServiceRow({ service, onBook }) {
  return (
    <div
      data-testid={`service-row-${service.id}`}
      style={{ display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center", padding: "20px 0", borderBottom: `1px solid ${BORDER}`, gap: "16px" }}
    >
      <div>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.15rem", fontWeight: 500, color: CHARCOAL, lineHeight: 1.3 }}>{service.name}</p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", marginTop: "4px" }}>
          {service.mode}
          {service.location !== "Online" && <span style={{ color: GOLD, marginLeft: "8px" }}>· {service.location}</span>}
        </p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.82rem", color: "#4A4A4A", marginTop: "6px", lineHeight: 1.55 }}>{service.description}</p>
      </div>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.1rem", fontWeight: 600, color: CHARCOAL, whiteSpace: "nowrap" }}>
        ₹{service.price.toLocaleString("en-IN")}{service.priceSuffix || ""}
      </p>
      <button
        onClick={() => onBook(service)}
        data-testid={`pay-btn-${service.id}`}
        style={{ backgroundColor: CHARCOAL, color: OFF_WHITE, padding: "10px 20px", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", border: "none", cursor: "pointer", whiteSpace: "nowrap", transition: "background-color 0.3s" }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CHARCOAL; }}
      >
        Pay & Book
      </button>
    </div>
  );
}

function VastuSubSection({ title, services, onBook, bg }) {
  return (
    <div style={{ backgroundColor: bg || OFF_WHITE, border: `1px solid ${BORDER}`, marginBottom: "24px" }}>
      <div style={{ padding: "20px 32px", borderBottom: `1px solid ${BORDER}`, backgroundColor: "#F5F3EB" }}>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>{title}</p>
      </div>
      <div style={{ padding: "0 32px" }}>
        {services.map((s) => <ServiceRow key={s.id} service={s} onBook={onBook} />)}
      </div>
    </div>
  );
}

export default function ConsultationsPage() {
  const [selectedService, setSelectedService] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const handleBook = (service) => { setSelectedService(service); setModalOpen(true); };
  useSEO({
    title: "Consultation Fees & Pricing | Akkshay Narula — Astrologer & Vastu Consultant",
    description: "Full pricing for Vedic astrology, numerology, premium kundli analysis, yearly astrology, Vastu (residential, commercial, industrial), outstation & international Vastu, and astrocartography consultations by Akkshay Narula. 100% advance payment.",
    keywords: "astrology consultation fees India, vastu consultant charges India, premium kundli analysis, yearly astrology consultation, numerology reading price, astrocartography price India, vastu consultant fees India",
    canonical: "/consultations",
  });

  return (
    <main data-testid="consultations-page" style={{ backgroundColor: OFF_WHITE }}>
      {/* Header */}
      <section style={{ padding: "72px 0 56px", borderBottom: `1px solid ${BORDER}`, backgroundColor: "#F5F3EB" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>Full Fee Structure</p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, color: CHARCOAL, lineHeight: 1.1, maxWidth: "600px" }}>
            Consultations & Fees
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", marginTop: "16px", lineHeight: 1.7, maxWidth: "500px" }}>
            All fees are fixed. 100% advance payment mandatory. Consultations by prior appointment only.
          </p>
        </div>
      </section>

      {/* Astrology & Numerology */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }} data-testid="astrology-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Vedic Astrology & Numerology" title="Astrology & Numerology Services" />
          {ASTROLOGY_SERVICES.map((s) => <ServiceRow key={s.id} service={s} onBook={handleBook} />)}
        </div>
      </section>

      {/* Premium & Yearly Kundli */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}`, backgroundColor: "#F5F3EB" }} data-testid="premium-kundli-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Advanced Kundli Services" title="Premium & Yearly Kundli Consultations" />
          <ServiceRow service={PREMIUM_KUNDLI} onBook={handleBook} />
          <ServiceRow service={YEARLY_ASTROLOGY} onBook={handleBook} />
        </div>
      </section>

      {/* Kundli Comparison */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }} data-testid="kundli-comparison-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Compare" title="Kundli Services Comparison" />
          <KundliComparison />
        </div>
      </section>

      {/* Astrocartography */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}`, backgroundColor: "#F5F3EB" }} data-testid="astrocartography-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Location Analysis" title="Astrocartography Consultation" />
          {ASTROCARTOGRAPHY_SERVICES.map((s) => <ServiceRow key={s.id} service={s} onBook={handleBook} />)}
          <div style={{ marginTop: "20px", padding: "20px 24px", border: `1px solid ${BORDER}`, backgroundColor: OFF_WHITE }}>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "10px" }}>Includes Analysis For</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {["Career Growth", "Relocation Planning", "Best City / Country Selection", "Business Expansion Zones"].map((item) => (
                <div key={item} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={{ width: "5px", height: "5px", backgroundColor: GOLD, borderRadius: "50%", flexShrink: 0 }} />
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.82rem", color: "#4A4A4A" }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vastu */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }} data-testid="vastu-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Space & Energy Alignment" title="Vastu Consultation Services" />
          <VastuSubSection title="1. Residential Vastu" services={VASTU_RESIDENTIAL} onBook={handleBook} />
          <VastuSubSection title="2. Commercial Vastu" services={VASTU_COMMERCIAL} onBook={handleBook} />
          <VastuSubSection title="3. Industrial Vastu" services={VASTU_INDUSTRIAL} onBook={handleBook} />
        </div>
      </section>

      {/* Outstation & International Vastu */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}`, backgroundColor: "#F5F3EB" }} data-testid="vastu-outstation-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Travel & On-Site Visits" title="Outstation & International Vastu" />
          <VastuOutstationCards />
        </div>
      </section>

      {/* Vastu Enquiry */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }} data-testid="vastu-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader overline="Project Enquiry" title="Vastu Project Enquiry" />
          <VastuEnquiryForm />
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: CHARCOAL, padding: "64px 0", textAlign: "center" }}>
        <div className="max-w-xl mx-auto px-6">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 500, color: "#FDFCF8", marginBottom: "20px" }}>Ready to Book?</h2>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#8A8A8A", marginBottom: "32px", lineHeight: 1.7 }}>
            Select a service above to pay and book, or message on WhatsApp to discuss your requirements first.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="consultations-whatsapp-cta"
            style={{ backgroundColor: GOLD, color: CHARCOAL, padding: "15px 32px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "8px" }}
          >
            Request via WhatsApp <ArrowRight size={14} />
          </a>
        </div>
      </section>

      <PaymentModal service={selectedService} open={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}
